import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Battery, Zap, Star, ArrowRight, ShoppingCart } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n ? `₹${Number(n).toLocaleString('en-IN')}` : null;

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/catalog/website/products`);
        setProducts(res.data);
      } catch (e) { console.error('ProductsPage load failed', e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-slate-600 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/products" className="flex items-center gap-2.5" data-testid="products-logo">
            <div className="w-8 h-8 rounded bg-[#0061dd] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight">Voltide</span>
          </Link>
          <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors">
            Dashboard
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-10">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight" data-testid="products-heading">
          Energy Storage<br />
          <span className="text-[#0061dd]">Solutions</span>
        </h1>
        <p className="text-slate-400 mt-4 max-w-xl text-base leading-relaxed">
          Battery Energy Storage Systems engineered for reliability. Buy outright or rent with flexible terms.
        </p>
      </section>

      {/* Product Grid */}
      <section className="max-w-7xl mx-auto px-6 pb-20">
        {products.length === 0 ? (
          <div className="text-center py-24 border border-slate-800 rounded-lg">
            <Battery className="w-12 h-12 mx-auto mb-4 text-slate-700" />
            <p className="text-slate-500">No products available at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="products-grid">
            {products.map(p => <ProductCard key={p.voltide_sku_code} product={p} />)}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/60 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-600">
          <span>&copy; {new Date().getFullYear()} Voltide Solutions Pvt. Ltd. All rights reserved.</span>
          <span>Prices starting from. Final quote may vary with SLA/site conditions.</span>
        </div>
      </footer>
    </div>
  );
}

function ProductCard({ product: p }) {
  const slug = p.public_slug || p.voltide_sku_code;
  const hasBuy = p.buy_from_price_ex_gst != null;
  const hasRent = p.rent_from_inr_per_kwh_month != null;

  return (
    <Link to={`/products/${slug}`} className="group block" data-testid={`product-card-${p.voltide_sku_code}`}>
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 h-full transition-all duration-200 hover:border-slate-600 hover:bg-slate-900/80">
        {/* Top row: badges */}
        <div className="flex items-center justify-between mb-4">
          <span className="font-mono text-[10px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded">{p.voltide_sku_code}</span>
          <div className="flex items-center gap-1.5">
            {p.featured && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
            <Badge className="text-[9px] bg-slate-800 text-slate-400 border-slate-700">{p.sku_type}</Badge>
          </div>
        </div>

        {/* Product name */}
        <h3 className="text-base font-semibold text-white mb-1 leading-snug group-hover:text-[#0061dd] transition-colors">
          {p.public_product_name}
        </h3>

        {/* Specs */}
        <div className="flex items-center gap-3 text-xs text-slate-500 mb-5">
          <span className="flex items-center gap-1"><Zap className="w-3 h-3" />{p.rated_power_kw} kW</span>
          <span className="flex items-center gap-1"><Battery className="w-3 h-3" />{p.battery_energy_kwh} kWh</span>
        </div>

        {/* Pricing section */}
        <div className="space-y-3 pt-4 border-t border-slate-800">
          {hasBuy && (
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Buy (from)</span>
              <div className="text-right">
                <span className="font-mono text-sm font-semibold text-white">{fmt(p.buy_from_price_ex_gst)}</span>
                <span className="text-[10px] text-slate-500 ml-1">ex-GST</span>
              </div>
            </div>
          )}
          {hasRent && (
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Rent (from)</span>
              <div className="text-right">
                <span className="font-mono text-sm font-semibold text-emerald-400">₹{Number(p.rent_from_inr_per_kwh_month).toFixed(2)}</span>
                <span className="text-[10px] text-slate-500 ml-1">/kWh/mo</span>
              </div>
            </div>
          )}
          {!hasBuy && !hasRent && (
            <p className="text-xs text-slate-600 italic">Pricing available on request</p>
          )}
        </div>

        {/* CTA */}
        <div className="flex items-center justify-between mt-5 pt-3 border-t border-slate-800/50">
          <span className="text-[10px] text-slate-600">
            {p.checkout_eligible && <ShoppingCart className="w-3 h-3 inline mr-1" />}
            {p.rent_min_disclaimer}
          </span>
          <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-[#0061dd] group-hover:translate-x-0.5 transition-all" />
        </div>
      </div>
    </Link>
  );
}
